import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, Plus, Trash2, Pencil, Check, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "../hooks/use-toast";

const KINDS = [
  { value: "back_in_stock", label: "IN · Back in stock" },
  { value: "stock_replenished", label: "IN · Restocked" },
  { value: "stock_drawn", label: "OUT · Drawn" },
  { value: "out_of_stock", label: "OUT · Out of stock" },
];
const kindLabel = (v) => (KINDS.find((k) => k.value === v) || {}).label || v;
const kindColor = (v) =>
  v === "stock_drawn" ? "text-amber-700" : v === "out_of_stock" ? "text-rose-700" : "text-emerald-700";

const emptyForm = () => ({
  kind: "stock_replenished",
  qty: "",
  on_hand: "",
  ts: new Date(Date.now() + 4 * 3600e3).toISOString().slice(0, 16), // Oman now
  expiry_date: "",
  pallet_id: "",
  location_id: "",
});

const Fields = ({ form, setForm }) => (
  <div className="space-y-2">
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
        className="col-span-2 sm:col-span-1 border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="ledger-form-kind">
        {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
      </select>
      <input type="number" min="0" placeholder="Qty" value={form.qty}
        onChange={(e) => setForm({ ...form, qty: e.target.value })}
        className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="ledger-form-qty" />
      <input type="number" min="0" placeholder="On hand" value={form.on_hand}
        onChange={(e) => setForm({ ...form, on_hand: e.target.value })}
        className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="ledger-form-onhand" />
      <input type="datetime-local" value={form.ts}
        onChange={(e) => setForm({ ...form, ts: e.target.value })}
        className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="ledger-form-ts" />
      <input type="date" value={form.expiry_date} title="Exp Date"
        onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
        className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="ledger-form-exp" />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <input type="text" placeholder="Pallet # (optional)" value={form.pallet_id}
        onChange={(e) => setForm({ ...form, pallet_id: e.target.value })}
        className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="ledger-form-pallet" />
      <input type="text" placeholder="Location (optional)" value={form.location_id}
        onChange={(e) => setForm({ ...form, location_id: e.target.value })}
        className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="ledger-form-location" />
    </div>
  </div>
);

export const LedgerEditor = ({ product, onClose, API, authHeaders, canEdit = true }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [exportBusy, setExportBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stock/ledger/${product.id}`, { headers: authHeaders });
      setEntries(data.entries || []);
    } catch (e) {
      toast({ title: "Failed to load ledger", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally { setLoading(false); }
  }, [API, authHeaders, product.id]);

  useEffect(() => { load(); }, [load]);

  const payloadOf = (f) => ({
    kind: f.kind,
    qty: f.qty === "" ? null : Number(f.qty),
    on_hand: f.on_hand === "" ? null : Number(f.on_hand),
    ts: f.ts || null,
    expiry_date: f.expiry_date || null,
    pallet_id: (f.pallet_id || "").trim() || null,
    location_id: (f.location_id || "").trim() || null,
  });

  const addEntry = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/stock/ledger/${product.id}`, payloadOf(addForm), { headers: authHeaders });
      setAddForm(emptyForm());
      await load();
      toast({ title: "Ledger entry added" });
    } catch (e) {
      toast({ title: "Failed to add entry", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally { setBusy(false); }
  };

  const saveEdit = async (id) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/stock/ledger/entry/${id}`, payloadOf(editForm), { headers: authHeaders });
      setEditingId(null);
      await load();
      toast({ title: "Ledger entry updated" });
    } catch (e) {
      toast({ title: "Failed to update", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this ledger entry? This cannot be undone.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/stock/ledger/entry/${id}`, { headers: authHeaders });
      await load();
      toast({ title: "Ledger entry deleted" });
    } catch (e) {
      toast({ title: "Failed to delete", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally { setBusy(false); }
  };

  // Feb 2026 — Export the ledger as PDF / Excel. Both endpoints are
  // read-only, so BOTH view and edit roles get the buttons. Failure
  // (403 / server error) drops a toast instead of throwing.
  const exportLedger = async (kind) => {
    setExportBusy(kind);
    try {
      const res = await axios.get(
        `${API}/admin/stock/ledger/${product.id}/export.${kind}`,
        { headers: authHeaders, responseType: "blob" },
      );
      const mime = kind === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (product.name || product.id).replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 48);
      a.download = `ledger-${safe}.${kind}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: `Ledger ${kind.toUpperCase()} downloaded` });
    } catch (e) {
      toast({
        title: `Failed to export ${kind.toUpperCase()}`,
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setExportBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-neutral-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="ledger-editor-modal">
      <div className="bg-white w-full sm:max-w-3xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-bold text-neutral-900">In / Out Ledger — {product.name}</h2>
            {/* Feb 2026 — accounting summary strip: SKU / Pack / UOM
                mirror the same header on the exported PDF + Excel. */}
            <p
              className="text-[11px] text-neutral-600 mt-0.5"
              data-testid="ledger-modal-summary"
            >
              <span>SKU: <span className="font-mono text-neutral-800">{product.sku || '—'}</span></span>
              <span className="mx-2 text-neutral-300">·</span>
              <span>Pack: <span className="font-mono text-neutral-800">{product.size || '—'}</span></span>
              <span className="mx-2 text-neutral-300">·</span>
              <span>UOM: <span className="uppercase text-neutral-800">{product.uom || 'ctn'}</span></span>
            </p>
            <p className="text-[10px] text-neutral-400 mt-0.5">Manual corrections show on the live report ledger. Actual stock &amp; batches are NOT changed.</p>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Feb 2026 — export buttons available to BOTH view-only
                and edit roles (both endpoints are read-only). */}
            <button
              onClick={() => exportLedger("pdf")}
              disabled={!!exportBusy}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-neutral-200 hover:bg-neutral-50 text-neutral-600 text-xs font-semibold disabled:opacity-50"
              title="Export ledger as PDF"
              data-testid="ledger-export-pdf-btn"
            >
              <FileText className="w-3.5 h-3.5" /> PDF
            </button>
            <button
              onClick={() => exportLedger("xlsx")}
              disabled={!!exportBusy}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-neutral-200 hover:bg-neutral-50 text-emerald-700 text-xs font-semibold disabled:opacity-50"
              title="Export ledger as Excel"
              data-testid="ledger-export-xlsx-btn"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
            </button>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-neutral-100 text-neutral-500" data-testid="ledger-editor-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          {canEdit && (
            <div className="rounded-lg border border-[#0078D4]/30 bg-[#0078D4]/5 p-3 space-y-2" data-testid="ledger-add-form">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#0078D4]">Add manual entry</div>
              <Fields form={addForm} setForm={setAddForm} />
              <button onClick={addEntry} disabled={busy}
                className="inline-flex items-center gap-1.5 bg-[#0078D4] text-white text-xs font-bold px-3 py-1.5 rounded-md hover:bg-[#106EBE] disabled:opacity-50"
                data-testid="ledger-add-btn">
                <Plus className="w-3.5 h-3.5" /> Add entry
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center text-xs text-neutral-500 py-6">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="text-center text-xs text-neutral-500 py-6">No ledger entries for this item yet.</div>
          ) : (
            <div
              className="overflow-x-auto rounded-lg border border-neutral-200"
              data-testid="ledger-entries-table"
            >
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 text-neutral-500 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-2 text-left font-semibold">Date &amp; Time</th>
                    <th className="p-2 text-right font-semibold text-emerald-700">IN</th>
                    <th className="p-2 text-right font-semibold text-amber-700">OUT</th>
                    <th className="p-2 text-right font-semibold">Balance</th>
                    <th className="p-2 text-left font-semibold">Pallet #</th>
                    <th className="p-2 text-left font-semibold">Location</th>
                    <th className="p-2 text-left font-semibold">Order No.</th>
                    <th className="p-2 text-left font-semibold">ASN</th>
                    <th className="p-2 text-left font-semibold">Reason</th>
                    {canEdit && <th className="p-2 w-20"></th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((en) => {
                    const editing = canEdit && editingId === en.id;
                    if (editing) {
                      return (
                        <tr
                          key={en.id}
                          className="bg-[#0078D4]/5 border-t border-neutral-100"
                          data-testid={`ledger-entry-${en.id}`}
                        >
                          <td colSpan={canEdit ? 10 : 9} className="p-2.5">
                            <div className="space-y-2">
                              <Fields form={editForm} setForm={setEditForm} />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => saveEdit(en.id)}
                                  disabled={busy}
                                  className="inline-flex items-center gap-1 bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-md disabled:opacity-50"
                                  data-testid="ledger-save-btn"
                                >
                                  <Check className="w-3.5 h-3.5" /> Save
                                </button>
                                <button
                                  onClick={() => setEditingId(null)}
                                  className="text-xs font-semibold text-neutral-500 px-2"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    // IN column: back_in_stock + stock_replenished.
                    // OUT column: stock_drawn + out_of_stock. Anything
                    // else stays blank in both slots (defensive).
                    const isIn = en.kind === "back_in_stock" || en.kind === "stock_replenished";
                    const isOut = en.kind === "stock_drawn" || en.kind === "out_of_stock";
                    const inQty = isIn && en.qty != null ? en.qty : null;
                    const outQty = isOut && en.qty != null ? en.qty : null;
                    return (
                      <tr
                        key={en.id}
                        className="border-t border-neutral-100 hover:bg-neutral-50 align-top"
                        data-testid={`ledger-entry-${en.id}`}
                      >
                        <td className="p-2 text-neutral-500 tabular-nums whitespace-nowrap">
                          {String(en.ts || "").replace("T", " ").slice(0, 19)}
                        </td>
                        <td
                          className="p-2 text-right tabular-nums font-semibold text-emerald-700"
                          data-testid={`ledger-entry-in-${en.id}`}
                        >
                          {inQty ?? ""}
                        </td>
                        <td
                          className="p-2 text-right tabular-nums font-semibold text-amber-700"
                          data-testid={`ledger-entry-out-${en.id}`}
                        >
                          {outQty ?? ""}
                        </td>
                        <td
                          className="p-2 text-right tabular-nums text-neutral-800"
                          data-testid={`ledger-entry-balance-${en.id}`}
                        >
                          {/*
                            Feb 2026 — server-side `_fill_running_balance`
                            stamps a computed `balance` on every row so
                            historical/backfilled rows without an on-hand
                            anchor still show a line-by-line running total
                            (prev + IN - OUT). Fall back to raw `on_hand`
                            for the edge case where the server left it null
                            (no anchor at all in the ledger).
                          */}
                          {en.balance ?? en.on_hand ?? ""}
                        </td>
                        <td
                          className="p-2 font-mono text-neutral-700"
                          data-testid={`ledger-entry-pallet-${en.id}`}
                        >
                          {en.pallet_id || <span className="text-neutral-300">—</span>}
                        </td>
                        <td
                          className="p-2 font-mono text-neutral-700"
                          data-testid={`ledger-entry-location-${en.id}`}
                        >
                          {en.location_id || <span className="text-neutral-300">—</span>}
                        </td>
                        <td
                          className="p-2 font-mono text-neutral-700 whitespace-nowrap"
                          data-testid={`ledger-entry-order-${en.id}`}
                        >
                          {en.order_number || <span className="text-neutral-300">—</span>}
                        </td>
                        <td
                          className="p-2 font-mono text-neutral-700 whitespace-nowrap"
                          data-testid={`ledger-entry-asn-${en.id}`}
                        >
                          {en.asn_number || <span className="text-neutral-300">—</span>}
                        </td>
                        <td className="p-2">
                          <span className={`font-semibold ${kindColor(en.kind)}`}>{kindLabel(en.kind)}</span>
                          {en.manual && (
                            <span className="ml-1.5 text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">manual</span>
                          )}
                        </td>
                        {canEdit && (
                          <td className="p-2">
                            <div className="flex gap-1 justify-end">
                              <button
                                onClick={() => {
                                  setEditingId(en.id);
                                  setEditForm({
                                    kind: en.kind,
                                    qty: en.qty ?? "",
                                    on_hand: en.on_hand ?? "",
                                    ts: en.ts || "",
                                    expiry_date: en.expiry_date || "",
                                    pallet_id: en.pallet_id || "",
                                    location_id: en.location_id || "",
                                  });
                                }}
                                className="p-1.5 rounded-md hover:bg-neutral-100 text-neutral-500"
                                title="Edit"
                                data-testid="ledger-edit-btn"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => remove(en.id)}
                                disabled={busy}
                                className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500"
                                title="Delete"
                                data-testid="ledger-delete-btn"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LedgerEditor;
